import { useEffect, useState } from 'react';
import axios from 'axios';
import { ZoomTable, ZoomBtn, ZoomModal, ZoomField, ZoomBadge } from './zoom/ZoomTable';
import { DestPicker, destLabel, destValid, useDestData, scopeToSite } from './IvrShared';
import { useCustomer, scopeToCustomer, withPrefix, loadCustomers } from './customerCtx';

/* IVR Menus (Queue Management). Tenant admins and PBX administrators build
   menus for their own tenant: greeting, keypad options, timeout, retries,
   invalid-key handling and optional direct extension dialling. Greetings are
   uploaded audio files converted by the server to phone quality. */
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '*', '#'];
const blankMenu = { id: null, menu_name: '', display_name: '', greeting_file: '', timeout_seconds: 10, max_retries: 3, direct_dial: 0,
  timeout_destination_type: '', timeout_destination: '', invalid_destination_type: '', invalid_destination: '', is_active: 1 };

export default function IvrMenusPage({ site }: { site?: any } = {}) {
  const [menus, setMenus] = useState<any[]>([]);
  const [optCounts, setOptCounts] = useState<Record<string, number>>({});
  const [refresh, setRefresh] = useState(0);
  const [edit, setEdit] = useState<any>(null);
  const [opts, setOpts] = useState<any[]>([]);
  const [origOpts, setOrigOpts] = useState<any[]>([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [upload, setUpload] = useState<{ name: string; file: File | null; msg: string }>({ name: '', file: null, msg: '' });
  const icust = useCustomer();
  const data = scopeToCustomer(scopeToSite(useDestData(refresh), site), icust);

  const load = async () => {
    try {
      const r = await axios.get('/api/asterisk/ivr'); const list = Array.isArray(r.data) ? r.data : [];
      setMenus(list);
      const counts: Record<string, number> = {};
      await Promise.all(list.map(async (m: any) => { try { const o = await axios.get(`/api/asterisk/ivr/${m.id}/options`); counts[m.id] = (o.data || []).length; } catch (_) {} }));
      setOptCounts(counts);
    } catch (e: any) { setErr(e?.response?.data?.error || 'Could not load IVR menus'); }
  };
  useEffect(() => { load(); }, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = async (m: any) => {
    setErr(''); setEdit({ ...blankMenu, ...m });
    if (m && m.id) {
      const o = await axios.get(`/api/asterisk/ivr/${m.id}/options`).catch(() => ({ data: [] }));
      const list = (o.data || []).map((x: any) => ({ ...x }));
      setOpts(list); setOrigOpts(list);
    } else { setOpts([{ digit: '1', destination_type: '', destination: '', description: '' }]); setOrigOpts([]); }
  };

  const doUpload = async () => {
    if (!upload.file || !upload.name.trim()) { setUpload({ ...upload, msg: 'Choose a file and give it a name.' }); return; }
    const fd = new FormData(); fd.append('file', upload.file); fd.append('name', upload.name.trim());
    if (icust.customerId) fd.append('customer_id', String(icust.customerId));
    setUpload({ ...upload, msg: 'Uploading and converting…' });
    try {
      const r = await axios.post('/api/ivr-prompts', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUpload({ name: '', file: null, msg: 'Uploaded: ' + r.data.name });
      setRefresh((x) => x + 1);
      if (edit) setEdit({ ...edit, greeting_file: r.data.sound });
    } catch (e: any) { setUpload({ ...upload, msg: e?.response?.data?.error || 'Upload failed' }); }
  };

  const save = async () => {
    setErr('');
    if (!edit.id && !String(edit.menu_name || edit.display_name).trim()) return setErr('Give the menu a name.');
    if (!destValid(edit.timeout_destination_type, edit.timeout_destination)) return setErr('Choose where callers go when they press nothing.');
    if (!destValid(edit.invalid_destination_type, edit.invalid_destination)) return setErr('Choose where callers go after too many invalid keys.');
    const digits = opts.map((o) => o.digit);
    if (new Set(digits).size !== digits.length) return setErr('Each key can only be used once.');
    for (const o of opts) if (!destValid(o.destination_type, o.destination)) return setErr(`Choose a destination for key ${o.digit}.`);
    setBusy(true);
    try {
      const body = edit.id ? { ...edit, menu_name: edit.menu_name || edit.display_name }
        : { ...edit, menu_name: withPrefix(icust.prefix, String(edit.menu_name || edit.display_name).trim().replace(/[^a-zA-Z0-9_-]+/g, '-')), customer_id: icust.customerId || undefined };
      let id = edit.id;
      if (id) await axios.put(`/api/asterisk/ivr/${id}`, body);
      else { const r = await axios.post('/api/asterisk/ivr', body); id = r.data.id; }
      for (const o of origOpts) if (!opts.find((x) => x.digit === o.digit)) await axios.delete(`/api/asterisk/ivr/${id}/options/${o.id}`);
      for (const o of opts) await axios.post(`/api/asterisk/ivr/${id}/options`, { digit: o.digit, destination_type: o.destination_type, destination: o.destination || '-', description: o.description || '' });
      setEdit(null); setRefresh((x) => x + 1); loadCustomers();
    } catch (e: any) { setErr(e?.response?.data?.error || 'Save failed'); }
    setBusy(false);
  };

  const remove = async (m: any) => {
    if (!window.confirm(`Delete IVR menu "${m.display_name || m.menu_name}"? Numbers routed to it will stop working.`)) return;
    try { await axios.delete(`/api/asterisk/ivr/${m.id}`); setRefresh((x) => x + 1); } catch (e: any) { alert(e?.response?.data?.error || 'Delete failed'); }
  };
  const removePrompt = async (p: any) => {
    if (!window.confirm(`Delete greeting "${p.name}"?`)) return;
    try { await axios.delete(`/api/ivr-prompts/${p.id}`); setRefresh((x) => x + 1); } catch (e: any) { alert(e?.response?.data?.error || 'Delete failed'); }
  };

  const greetingName = (s: string) => { const p = data.prompts.find((x: any) => x.sound === s); return p ? p.name : (s && s !== 'silence/1' ? s : '—'); };
  const columns = [
    { key: 'name', label: 'IVR menu', render: (m: any) => <div><div className="z-tbl-primary-cell">{m.display_name || m.menu_name}</div><div className="z-tbl-sub">{m.menu_name}</div></div> },
    { key: 'greeting', label: 'Greeting', render: (m: any) => greetingName(m.greeting_file) },
    { key: 'keys', label: 'Keys', render: (m: any) => String(optCounts[m.id] ?? '…') },
    { key: 'timeout', label: 'No input', render: (m: any) => `${m.timeout_seconds}s → ${destLabel(m.timeout_destination_type, m.timeout_destination, data)}` },
    { key: 'status', label: 'Status', render: (m: any) => <ZoomBadge text={Number(m.is_active) ? 'Active' : 'Off'} tone={Number(m.is_active) ? 'green' : 'gray'} /> },
  ];

  return (
    <>
      {edit && (
        <ZoomModal width={860} title={edit.id ? `Edit IVR — ${edit.display_name || edit.menu_name}` : 'New IVR menu'} onClose={() => setEdit(null)}
          footer={<><ZoomBtn onClick={() => setEdit(null)}>Cancel</ZoomBtn><ZoomBtn variant="primary" onClick={save}>{busy ? 'Saving…' : 'Save menu'}</ZoomBtn></>}>
          {err && <div style={{ color: '#b91c1c', marginBottom: 10 }}>{err}</div>}
          <div className="z-field-grid">
            <ZoomField label="Menu name" hint={edit.id ? 'Internal name cannot change' : 'Letters, numbers, - and _'}>
              <input value={edit.id ? edit.menu_name : (edit.menu_name || '')} disabled={!!edit.id} onChange={(e) => setEdit({ ...edit, menu_name: e.target.value })} placeholder="main-menu" /></ZoomField>
            <ZoomField label="Display name"><input value={edit.display_name || ''} onChange={(e) => setEdit({ ...edit, display_name: e.target.value })} placeholder="Main menu" /></ZoomField>
            <ZoomField label="Greeting" hint="Played while callers choose an option">
              <select value={edit.greeting_file || ''} onChange={(e) => setEdit({ ...edit, greeting_file: e.target.value })}>
                <option value="">— no greeting (silence) —</option>
                {data.prompts.map((p: any) => <option key={p.id} value={p.sound}>{p.name}</option>)}
              </select></ZoomField>
            <ZoomField label="Wait for a key (seconds)"><input type="number" min={2} max={60} value={edit.timeout_seconds} onChange={(e) => setEdit({ ...edit, timeout_seconds: e.target.value })} /></ZoomField>
            <ZoomField label="Replay the menu (times)" hint="After this many invalid or missing keys the call moves on"><input type="number" min={1} max={9} value={edit.max_retries} onChange={(e) => setEdit({ ...edit, max_retries: e.target.value })} /></ZoomField>
            <ZoomField label="Options">
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={!!Number(edit.direct_dial)} onChange={(e) => setEdit({ ...edit, direct_dial: e.target.checked ? 1 : 0 })} style={{ width: 'auto' }} /> Callers may dial an extension number directly</label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}><input type="checkbox" checked={!!Number(edit.is_active)} onChange={(e) => setEdit({ ...edit, is_active: e.target.checked ? 1 : 0 })} style={{ width: 'auto' }} /> Menu active</label>
            </ZoomField>
          </div>
          <h4 style={{ margin: '16px 0 6px' }}>Keypad options</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={{ textAlign: 'left', width: 70 }}>Key</th><th style={{ textAlign: 'left' }}>Goes to</th><th style={{ textAlign: 'left', width: 200 }}>Description</th><th /></tr></thead>
            <tbody>{opts.map((o, i) => (
              <tr key={i}>
                <td><select value={o.digit} onChange={(e) => setOpts(opts.map((x, j) => (j === i ? { ...x, digit: e.target.value } : x)))}>{KEYS.map((k) => <option key={k} value={k}>{k}</option>)}</select></td>
                <td style={{ padding: '4px 0' }}><DestPicker required type={o.destination_type} value={o.destination} data={data} excludeIvr={edit.menu_name}
                  onChange={(t, v) => setOpts(opts.map((x, j) => (j === i ? { ...x, destination_type: t, destination: v } : x)))} /></td>
                <td><input value={o.description || ''} placeholder="e.g. Sales" onChange={(e) => setOpts(opts.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} /></td>
                <td><ZoomBtn variant="danger" onClick={() => setOpts(opts.filter((_, j) => j !== i))}>Remove</ZoomBtn></td>
              </tr>))}</tbody>
          </table>
          <div style={{ marginTop: 8 }}><ZoomBtn onClick={() => { const free = KEYS.find((k) => !opts.find((o) => o.digit === k)); if (free) setOpts([...opts, { digit: free, destination_type: '', destination: '', description: '' }]); }}>+ Add key</ZoomBtn></div>
          <h4 style={{ margin: '16px 0 6px' }}>When the caller presses nothing</h4>
          <DestPicker required type={edit.timeout_destination_type} value={edit.timeout_destination} data={data} excludeIvr={edit.menu_name}
            onChange={(t, v) => setEdit({ ...edit, timeout_destination_type: t, timeout_destination: v })} />
          <h4 style={{ margin: '16px 0 6px' }}>After too many invalid keys</h4>
          <DestPicker required type={edit.invalid_destination_type} value={edit.invalid_destination} data={data} excludeIvr={edit.menu_name}
            onChange={(t, v) => setEdit({ ...edit, invalid_destination_type: t, invalid_destination: v })} />
        </ZoomModal>
      )}

      <ZoomTable title="IVR Menus" subtitle="Menus callers hear when they reach a number — press 1 for sales, 2 for support… Route a number to a menu on the DIDs page."
        actions={<ZoomBtn variant="primary" onClick={() => openEdit({ ...blankMenu })}>+ New IVR menu</ZoomBtn>}
        columns={columns} rows={menus.filter((m: any) => icust.match('ivr', m.id))} rowKey={(m: any) => m.id} selectable={false}
        rowActions={(m: any) => (<><ZoomBtn onClick={() => openEdit(m)}>Edit</ZoomBtn><ZoomBtn variant="danger" onClick={() => remove(m)}>Delete</ZoomBtn></>)}
        emptyText="No IVR menus yet" />

      <div style={{ height: 18 }} />
      <ZoomTable title="Greetings" subtitle="Upload MP3 or WAV recordings (max 10 MB). They are converted to phone quality and can be used by IVR menus, business hours and holidays."
        actions={<div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input style={{ width: 180 }} placeholder="Greeting name" value={upload.name} onChange={(e) => setUpload({ ...upload, name: e.target.value })} />
          <input type="file" accept="audio/*,.mp3,.wav,.m4a" onChange={(e) => setUpload({ ...upload, file: (e.target.files && e.target.files[0]) || null })} />
          <ZoomBtn variant="primary" onClick={doUpload}>Upload</ZoomBtn>
          {upload.msg && <span style={{ fontSize: 12 }}>{upload.msg}</span>}
        </div>}
        columns={[
          { key: 'n', label: 'Greeting', render: (p: any) => <div className="z-tbl-primary-cell">{p.name}</div> },
          { key: 'd', label: 'Length', render: (p: any) => (p.duration_sec ? `${Number(p.duration_sec).toFixed(1)} s` : '—') },
          { key: 'c', label: 'Added', render: (p: any) => String(p.created_at || '').slice(0, 10) },
        ]}
        rows={data.prompts} rowKey={(p: any) => p.id} selectable={false}
        rowActions={(p: any) => <ZoomBtn variant="danger" onClick={() => removePrompt(p)}>Delete</ZoomBtn>}
        emptyText="No greetings uploaded yet" />
    </>
  );
}
